import { useStartKYCMutation } from '@/src/mutations/earning/add-verification-details'
// import { useMaskedVerificationDetails } from '@/src/queries/earnings/masked-verification'
import { useVerificationStatusQuery } from '@/src/queries/earnings/verification-status'

import {
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Input,
  FormLabel,
  FormControl,
  FormErrorMessage,
  Text,
  Box,
} from '@chakra-ui/react'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useRef,
  useState,
  type ComponentProps,
} from 'react'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { RiVerifiedBadgeFill } from 'react-icons/ri'
import { useUser } from '@/src/store/user-store'

interface IDDetailsProps {
  aadharNumber: string
  gstNumber: string
  name: string
  accountNo: string
  ifsc: string
  pan: string
  dob: string
}

const inputStyles = {
  size: {
    base: 'md',
  },
  rounded: 'md',
  border: '1px solid #E5E5E5',
  px: {
    base: '3',
    md: '4',
  },
  fontSize: {
    base: 'sm',
    md: 'md',
  },
  mt: 1.5,
}

const labelStyles = {
  fontSize: {
    base: 'sm',
    md: 'md',
  },
  fontWeight: '500',
  marginBottom: '1px',
  color: '#3E3E3E',
  opacity: 0.8,
}

const parseIsoDate = (value?: string) => {
  if (!value) return null
  const [year, month, day] = value.split('-').map(Number)
  if (
    Number.isNaN(year) ||
    Number.isNaN(month) ||
    Number.isNaN(day) ||
    month < 1 ||
    month > 12 ||
    day < 1 ||
    day > 31
  ) {
    return null
  }
  return new Date(year, month - 1, day)
}

const toIsoDate = (date: Date) => {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

const formatDobForPayload = (dob?: string | null) => {
  if (!dob) return ''

  if (/^\d{4}-\d{2}-\d{2}$/.test(dob)) {
    const [year, month, day] = dob.split('-')
    return `${day}/${month}/${year}`
  }

  if (/^\d{2}\/\d{2}\/\d{4}$/.test(dob)) {
    return dob
  }

  const parsed = new Date(dob)
  if (Number.isNaN(parsed.getTime())) return ''

  const day = String(parsed.getDate()).padStart(2, '0')
  const month = String(parsed.getMonth() + 1).padStart(2, '0')
  const year = parsed.getFullYear()
  return `${day}/${month}/${year}`
}

const DatePickerInput = forwardRef<
  HTMLInputElement,
  ComponentProps<typeof Input>
>((props, ref) => (
  <Input
    ref={ref}
    {...inputStyles}
    _placeholder={{ color: 'black', opacity: 1 }}
    {...props}
  />
))
DatePickerInput.displayName = 'DatePickerInput'

export default function VerifyID({
  isOpen,
  onClose,
  onVerifiedClose,
  handleMaxAttemptsFlow,
}: {
  isOpen: boolean
  onClose: () => void
  onVerifiedClose?: () => void
  handleMaxAttemptsFlow?: () => void
}) {
  // const maskedDetails = useMaskedVerificationDetails()
  const verificationStatus = useVerificationStatusQuery()
  const submitDetails = useStartKYCMutation()
  const shouldShowVerificationStatusError = useRef(false)
  const hasShownKycSuccessToast = useRef(false)
  const hasCompletedKycSuccessTransition = useRef(false)
  const [mutationKycStatus, setMutationKycStatus] = useState<string>()
  const { user } = useUser()
  const isBusinessUser = String(user?.isBusinessUser) === '1'
  const today = new Date()
  const kycStatus = mutationKycStatus ?? verificationStatus.data?.data?.status

  const methods = useForm<IDDetailsProps>({
    shouldFocusError: true,
    values: {
      aadharNumber: '',
      gstNumber: '',
      accountNo: '',
      name: '',
      ifsc: '',
      pan: '',
      dob: '',
    },
  })

  const {
    register,
    setError,
    setValue,
    trigger,
    clearErrors,
    handleSubmit,
    control,
    formState: { errors },
  } = methods
  const clearRootError = useCallback(
    () => clearErrors('root' as never),
    [clearErrors]
  )
  const handleKycSuccessToastClose = useCallback(() => {
    if (hasCompletedKycSuccessTransition.current) return

    hasCompletedKycSuccessTransition.current = true
    onVerifiedClose?.()
  }, [onVerifiedClose])

  const showKycSuccessToast = useCallback(() => {
    onClose()
    toast.success('KYC Verification is successfully Done', {
      onAutoClose: handleKycSuccessToastClose,
      onDismiss: handleKycSuccessToastClose,
    })
  }, [handleKycSuccessToastClose, onClose])

  const handleProceed = () => {
    const requiredFieldsBeforeProceed: Array<keyof IDDetailsProps> =
      isBusinessUser
        ? ['name', 'dob', 'accountNo', 'ifsc', 'pan', 'gstNumber']
        : ['name', 'accountNo', 'ifsc', 'pan', 'aadharNumber']

    void trigger(requiredFieldsBeforeProceed, {
      shouldFocus: true,
    }).then((isFormValid) => {
      if (!isFormValid) {
        return
      }

      shouldShowVerificationStatusError.current = true
      clearRootError()
      void handleSubmit(onSubmit)()
    })
  }

  const onSubmit = (data: IDDetailsProps) => {
    const formattedDob = formatDobForPayload(
      isBusinessUser ? data.dob : user?.dob
    )

    const payloadData = {
      name: data.name,
      accountNo: data.accountNo,
      ifsc: data.ifsc,
      pan: data.pan,
      ...(!isBusinessUser
        ? {
            aadhaarNumber: data.aadharNumber,
          }
        : {
            gstNumber: data.gstNumber,
          }),
    }

    const payload = {
      ...payloadData,
      name:
        verificationStatus.data?.panVerification == 'verified'
          ? 'xxxx'
          : data.name,
      pan:
        verificationStatus.data?.panVerification == 'verified'
          ? 'xxxx'
          : data.pan,
      accountNo:
        verificationStatus.data?.bankVerification == 'verified'
          ? 'xxxx'
          : data.accountNo,
      ifsc:
        verificationStatus.data?.bankVerification == 'verified'
          ? 'xxxx'
          : data.ifsc,
      ...(!isBusinessUser && {
        aadhaarNumber:
          verificationStatus.data?.aadhaarVerification == 'verified'
            ? 'xxxx'
            : data.aadharNumber,
      }),
      ...(isBusinessUser && {
        gstNumber:
          verificationStatus.data?.gstVerification == 'verified'
            ? 'xxxx'
            : data.gstNumber,
      }),
      dob: formattedDob,
    }

    // console.log('Submitting payload:', payload)

    submitDetails.mutate(
      {
        ...payload,
        errorCallback: (error, message) => {
          setError(error, {
            type: 'manual',
            message: message,
          })
        },
      },
      {
        onSuccess: (response) => {
          if (response.data?.redirectUrl) {
            window.location.assign(response.data.redirectUrl)
            return
          } else {
            const status = response.data?.status
            setMutationKycStatus(status)

            if (status === 'success') {
              hasShownKycSuccessToast.current = true
              showKycSuccessToast()
            }

            if (
              status !== 'processing' &&
              status !== 'failed' &&
              status !== 'success' &&
              response.message
            ) {
              toast.success(response.message)
              return
            }

            void verificationStatus.refetch().finally(() => {
              setMutationKycStatus(undefined)
            })
            return
          }
        },
        onError: (error) => {
          toast.error(error.message)
          setError('root', {
            type: 'manual',
            message: error.message,
          })
        },
      }
    )
  }

  useEffect(() => {
    if (isBusinessUser) {
      setValue('aadharNumber', '')
      setValue('dob', '')
      clearErrors('aadharNumber')
    } else {
      setValue('dob', '')
    }
  }, [clearErrors, isBusinessUser, setValue, user?.dob])

  useEffect(() => {
    const savedDetails = verificationStatus.data?.data

    if (!savedDetails || savedDetails.status === 'success') return

    setValue('name', savedDetails.name ?? '')
    setValue('pan', savedDetails.pan ?? '')
    setValue('accountNo', savedDetails.accountNo ?? '')
    setValue('ifsc', savedDetails.ifsc ?? '')

    if (isBusinessUser) {
      setValue('gstNumber', savedDetails.gstNumber ?? '')
    } else {
      setValue('aadharNumber', savedDetails.aadhaarNumber ?? '')
    }
  }, [isBusinessUser, setValue, verificationStatus.data])

  useEffect(() => {
    if (kycStatus === 'success' && !hasShownKycSuccessToast.current) {
      showKycSuccessToast()
      hasShownKycSuccessToast.current = true
    } else if (kycStatus !== 'success') {
      hasShownKycSuccessToast.current = false
      hasCompletedKycSuccessTransition.current = false
    }
  }, [kycStatus, showKycSuccessToast])

  useEffect(() => {
    if (verificationStatus.data?.panVerification == 'verified') {
      // setValue('pan', maskedDetails.data?.maskedPan || '')
      // setValue('name', maskedDetails.data?.name || '')
      setValue('pan', 'xxxx')
      setValue('name', 'xxxx')
    }
    if (verificationStatus.data?.bankVerification == 'verified') {
      // setValue('accountNo', maskedDetails.data?.maskedBankAccount || '')
      // setValue('ifsc', maskedDetails.data?.ifsc || '')
      setValue('accountNo', 'xxxx')
      setValue('ifsc', 'xxxx')
    }
    if (verificationStatus.data?.aadhaarVerification == 'verified') {
      setValue('aadharNumber', 'xxxx')
    }
    if (verificationStatus.data?.gstVerification == 'verified') {
      setValue('gstNumber', 'xxxx')
    }
  }, [verificationStatus.data, setValue]) // maskedDetails.data,

  useEffect(() => {
    shouldShowVerificationStatusError.current = false

    if (isOpen) {
      clearRootError()
    }
  }, [clearRootError, isOpen])

  useEffect(() => {
    if (
      shouldShowVerificationStatusError.current &&
      verificationStatus.data?.panVerification == 'failed'
    ) {
      setError('root', {
        type: 'manual',
        message: verificationStatus.data.panFailureReason,
      })
    } else if (
      shouldShowVerificationStatusError.current &&
      verificationStatus.data?.bankVerification == 'failed'
    ) {
      setError('root', {
        type: 'manual',
        message: verificationStatus.data.bankFailureReason,
      })
    }
    if (verificationStatus.data?.attemptLeft == '0') {
      handleMaxAttemptsFlow && handleMaxAttemptsFlow()
    }
  }, [verificationStatus.data, setError, handleMaxAttemptsFlow])

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size={['full', 'full', 'md']}
      isCentered
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader fontSize={['md', 'md', 'lg']}>
          Verify ID and Beneficiary Details
        </ModalHeader>
        <ModalCloseButton mt={1} />
        <hr />
        <ModalBody mt={2}>
          {verificationStatus.data && (
            <Text fontSize={['sm', 'sm', 'md']} mb={3} fontWeight={500}>
              You only have {verificationStatus.data.attemptLeft} attempts left
              to verify your ID and beneficiary details.
            </Text>
          )}
          <FormProvider {...methods}>
            <form
              className="mb-10"
              name="verify-details"
              onSubmit={(event) => {
                event.preventDefault()
                handleProceed()
              }}
            >
              {errors.root &&
                errors.root.message !==
                  'ID Verification is open from the 1st to 6th of every month.' && (
                  <Box>
                    <Text
                      color="red"
                      fontSize={{
                        base: 'sm',
                        md: 'md',
                      }}
                    >
                      <strong>Error: </strong>
                    </Text>
                    <Text
                      color="red"
                      fontSize={{
                        base: 'sm',
                        md: 'md',
                      }}
                      mb={3}
                    >
                      {errors.root.message}
                    </Text>
                  </Box>
                )}
              <FormControl mb={5} isRequired isInvalid={!!errors.name}>
                <FormLabel {...labelStyles}>
                  {isBusinessUser ? 'Business Name' : 'Name'} (Must match your
                  PAN Card)
                </FormLabel>
                <Input
                  {...inputStyles}
                  placeholder="Enter name here"
                  type="text"
                  {...register('name', {
                    required: 'Name is required',
                  })}
                  disabled={
                    verificationStatus.data?.panVerification == 'verified'
                  }
                />
                <FormErrorMessage>
                  <>{errors.name && errors.name.message}</>
                </FormErrorMessage>
                {verificationStatus.data?.panVerification == 'verified' && (
                  <Text
                    fontSize="sm"
                    fontWeight={500}
                    color="lyk-green"
                    mt={1}
                    display="flex"
                    gap={1}
                    marginTop={1}
                    alignItems="center"
                  >
                    <RiVerifiedBadgeFill color="#24BFA3" size="18px" />
                    Verified
                  </Text>
                )}
              </FormControl>
              <FormControl mb={5} isRequired isInvalid={!!errors.accountNo}>
                <FormLabel {...labelStyles}>Bank Account Number</FormLabel>
                <Input
                  {...inputStyles}
                  placeholder="Enter bank account number here"
                  type="text"
                  inputMode="numeric"
                  maxLength={18}
                  {...register('accountNo', {
                    required: 'Account Number is required',
                    pattern: {
                      value: /^\d{9,18}$/,
                      message: 'Enter a valid 9 to 18 digit Account Number',
                    },
                  })}
                  disabled={
                    verificationStatus.data?.bankVerification == 'verified'
                  }
                />
                <FormErrorMessage>
                  <>{errors.accountNo && errors.accountNo.message}</>
                </FormErrorMessage>
                {verificationStatus.data?.bankVerification == 'verified' && (
                  <Text
                    fontSize="sm"
                    fontWeight={500}
                    color="lyk-green"
                    mt={1}
                    display="flex"
                    gap={1}
                    marginTop={1}
                    alignItems="center"
                  >
                    <RiVerifiedBadgeFill color="#24BFA3" size="18px" />
                    Verified
                  </Text>
                )}
              </FormControl>
              <FormControl mb={5} isRequired isInvalid={!!errors.ifsc}>
                <FormLabel {...labelStyles}>Bank IFSC Code</FormLabel>
                <Input
                  {...inputStyles}
                  placeholder="Enter bank IFSC code here"
                  type="text"
                  maxLength={11}
                  {...register('ifsc', {
                    required: 'IFSC Code is required',
                    pattern: {
                      value: /^[A-Z]{4}0[A-Z0-9]{6}$/,
                      message: 'Enter a valid IFSC Code (e.g. ABCD0123456)',
                    },
                  })}
                  disabled={
                    verificationStatus.data?.bankVerification == 'verified'
                  }
                />
                <FormErrorMessage>
                  <>{errors.ifsc && errors.ifsc.message}</>
                </FormErrorMessage>
                {verificationStatus.data?.bankVerification == 'verified' && (
                  <Text
                    fontSize="sm"
                    fontWeight={500}
                    color="lyk-green"
                    mt={1}
                    display="flex"
                    gap={1}
                    marginTop={1}
                    alignItems="center"
                  >
                    <RiVerifiedBadgeFill color="#24BFA3" size="18px" />
                    Verified
                  </Text>
                )}
              </FormControl>
              <FormControl mb={5} isRequired isInvalid={!!errors.pan}>
                <FormLabel {...labelStyles}>PAN Number</FormLabel>
                <Input
                  {...inputStyles}
                  placeholder="Enter PAN number here"
                  type="text"
                  maxLength={10}
                  {...register('pan', {
                    required: 'PAN Number is required',
                    pattern: {
                      value: /^[A-Z]{5}[0-9]{4}[A-Z]$/,
                      message: 'Enter a valid PAN Number (e.g. ABCDE1234F)',
                    },
                  })}
                  disabled={
                    verificationStatus.data?.panVerification == 'verified'
                  }
                />
                <FormErrorMessage>
                  <>{errors.pan && errors.pan.message}</>
                </FormErrorMessage>
                {verificationStatus.data?.panVerification == 'verified' && (
                  <Text
                    fontSize="sm"
                    fontWeight={500}
                    color="lyk-green"
                    mt={1}
                    display="flex"
                    gap={1}
                    marginTop={1}
                    alignItems="center"
                  >
                    <RiVerifiedBadgeFill color="#24BFA3" size="18px" />
                    Verified
                  </Text>
                )}
              </FormControl>
              {isBusinessUser ? (
                <>
                  <FormControl mb={5} isRequired isInvalid={!!errors.gstNumber}>
                    <FormLabel {...labelStyles}>GST Number</FormLabel>
                    <Input
                      {...inputStyles}
                      placeholder="Enter GST number here"
                      type="text"
                      maxLength={15}
                      {...register('gstNumber', {
                        required: 'GST Number is required',
                        validate: (value) =>
                          verificationStatus.data?.gstVerification ==
                            'verified' ||
                          /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/i.test(
                            value
                          ) ||
                          'Enter a valid GST Number',
                        onChange: (event) => {
                          event.target.value = event.target.value
                            .toUpperCase()
                            .replace(/[^0-9A-Z]/g, '')
                            .slice(0, 15)
                        },
                      })}
                      disabled={
                        verificationStatus.data?.gstVerification == 'verified'
                      }
                    />
                    <FormErrorMessage>
                      <>{errors.gstNumber && errors.gstNumber.message}</>
                    </FormErrorMessage>
                    {verificationStatus.data?.gstVerification == 'verified' && (
                      <Text
                        fontSize="sm"
                        fontWeight={500}
                        color="lyk-green"
                        mt={1}
                        display="flex"
                        gap={1}
                        marginTop={1}
                        alignItems="center"
                      >
                        <RiVerifiedBadgeFill color="#24BFA3" size="18px" />
                        Verified
                      </Text>
                    )}
                  </FormControl>
                </>
              ) : (
                <FormControl
                  mb={5}
                  isRequired
                  isInvalid={!!errors.aadharNumber}
                >
                  <Box maxW={{ base: 'full', md: '520px' }} mx="auto">
                    <FormLabel {...labelStyles}>Aadhar Number</FormLabel>
                    <Input
                      {...inputStyles}
                      placeholder="Enter Aadhar number here"
                      type="text"
                      maxLength={12}
                      {...register('aadharNumber', {
                        required: 'Aadhar Number is required',
                        validate: (value) =>
                          verificationStatus.data?.aadhaarVerification ==
                            'verified' ||
                          /^\d{12}$/.test(value) ||
                          'Aadhar Number must be 12 digits',
                      })}
                      disabled={
                        verificationStatus.data?.aadhaarVerification ==
                        'verified'
                      }
                    />
                    <FormErrorMessage>
                      <>{errors.aadharNumber && errors.aadharNumber.message}</>
                    </FormErrorMessage>
                    {verificationStatus.data?.aadhaarVerification ==
                      'verified' && (
                      <Text
                        fontSize="sm"
                        fontWeight={500}
                        color="lyk-green"
                        mt={1}
                        display="flex"
                        gap={1}
                        marginTop={1}
                        alignItems="center"
                      >
                        <RiVerifiedBadgeFill color="#24BFA3" size="18px" />
                        Verified
                      </Text>
                    )}
                  </Box>
                </FormControl>
              )}
              {isBusinessUser && (
                <FormControl mb={5} isRequired isInvalid={!!errors.dob}>
                  <FormLabel {...labelStyles}>
                    Date of Incorporations / DOB (Must match your PAN Card)
                  </FormLabel>
                  <Controller
                    control={control}
                    name="dob"
                    rules={{
                      required: 'Date of Incorporation / DOB is required',
                      validate: (value) => {
                        if (value) {
                          const dob = parseIsoDate(value)
                          if (!dob) {
                            return 'Please select a valid date'
                          }
                          if (dob > today) {
                            return 'Date of Incorporation / DOB cannot be in the future'
                          }
                          const diff = new Date().getTime() - dob.getTime()
                          const age = Math.floor(
                            diff / (1000 * 60 * 60 * 24 * 365)
                          )
                        }
                        return true
                      },
                    }}
                    render={({ field }) => (
                      <DatePicker
                        selected={parseIsoDate(field.value)}
                        onChange={(date: Date | null) =>
                          field.onChange(
                            date instanceof Date ? toIsoDate(date) : ''
                          )
                        }
                        onBlur={field.onBlur}
                        name={field.name}
                        dateFormat="MM-dd-yyyy"
                        placeholderText="mm-dd-yyyy"
                        maxDate={today}
                        showMonthDropdown
                        showYearDropdown
                        dropdownMode="select"
                        customInput={
                          <DatePickerInput id="dob" isInvalid={!!errors.dob} />
                        }
                      />
                    )}
                  />
                  <FormErrorMessage>
                    <>{errors.dob && errors.dob.message}</>
                  </FormErrorMessage>
                </FormControl>
              )}
            </form>
          </FormProvider>
        </ModalBody>
        <hr />

        <ModalFooter>
          <Button
            width="100%"
            _hover={{ opacity: 0.7 }}
            size={['md', 'md', 'md']}
            fontWeight={500}
            onClick={handleProceed}
            bg={'lyk-green'}
            color={'white'}
            isLoading={submitDetails.isPending || kycStatus === 'processing'}
          >
            {kycStatus === 'failed' ? 'Resubmit' : 'Proceed'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
